import { useEffect, useState, useRef } from 'react'
import { useAuth } from '../lib/AuthContext'
import { useLang } from '../lib/LangContext'
import { supabase } from '../lib/supabase'
import { getAbonnement, getPaiements, soumettrePaiementManuel } from '../lib/api'

// ── Formules (affichage seul — le montant qui fait foi est côté serveur) ──
const FORMULES = [
  {
    id: 'mensuel', montant: 3000,
    label_fr: 'Mensuel', label_en: 'Monthly',
    unite_fr: 'mois', unite_en: 'month',
    desc_fr: 'Facturé chaque mois', desc_en: 'Billed monthly',
  },
  {
    id: 'annuel', montant: 30000,
    label_fr: 'Annuel', label_en: 'Annual',
    unite_fr: 'an', unite_en: 'year',
    desc_fr: '2 mois offerts — économisez 6 000 FCFA',
    desc_en: '2 months free — save 6,000 FCFA',
    badge_fr: '🏆 Meilleure offre', badge_en: '🏆 Best value',
    mois_fr: 'soit 2 500 FCFA/mois', mois_en: 'only 2,500 FCFA/month',
  },
]

// Repli manuel si le paiement automatique échoue
const METHODES_MANUELLES = [
  { id: 'orange_money', label: 'Orange Money', emoji: '🟠', numero: '+237 698 366 615', nom: 'Feugang Edmon' },
  { id: 'mtn_momo',     label: 'MTN MoMo',     emoji: '🟡', numero: '+237 651 687 345', nom: 'Fogang Marie Louise' },
]

function joursRestants(dateFin) {
  return Math.max(0, Math.ceil((new Date(dateFin) - new Date()) / 86400000))
}
function formatDate(d) {
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })
}

export default function Facturation() {
  const { user, boutique } = useAuth()
  const { lang } = useLang()

  const [abonnement, setAbonnement] = useState(null)
  const [paiements, setPaiements] = useState([])
  const [loading, setLoading] = useState(true)

  const [formule, setFormule] = useState('annuel')
  const [numero, setNumero] = useState('')
  const [etape, setEtape] = useState('choix')   // choix | attente | succes | echec
  const [envoi, setEnvoi] = useState(false)
  const [msg, setMsg] = useState(null)
  const [secondes, setSecondes] = useState(0)

  // Repli manuel
  const [manuel, setManuel] = useState(false)
  const [methodeManuelle, setMethodeManuelle] = useState(null)
  const [refManuelle, setRefManuelle] = useState('')

  const sondage = useRef(null)
  const chrono = useRef(null)

  useEffect(() => { if (user) charger() }, [user])
  useEffect(() => () => { clearInterval(sondage.current); clearInterval(chrono.current) }, [])

  async function charger() {
    const [{ data: abo }, { data: pay }] = await Promise.all([
      getAbonnement(user.id), getPaiements(user.id)
    ])
    setAbonnement(abo)
    setPaiements(pay || [])
    setLoading(false)
  }

  async function jetonSession() {
    const { data } = await supabase.auth.getSession()
    return data?.session?.access_token
  }

  // ── Lancer le paiement ────────────────────────────────────
  async function payer() {
    const chiffres = numero.replace(/\D/g, '')
    if (chiffres.length !== 9 && !(chiffres.startsWith('237') && chiffres.length === 12)) {
      setMsg({ type: 'error', text: lang === 'fr'
        ? 'Entre un numéro à 9 chiffres (ex : 698366615).'
        : 'Enter a 9-digit number.' })
      return
    }

    setEnvoi(true); setMsg(null)
    try {
      const token = await jetonSession()
      const r = await fetch('/api/tranzak-create-payment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ formule, numero: chiffres })
      })
      const data = await r.json()
      if (!r.ok) throw new Error(data.error || 'Erreur')

      // Cas où Tranzak demande une action web (OTP, CAPTCHA)
      if (data.paymentAuthUrl) window.open(data.paymentAuthUrl, '_blank')

      setEtape('attente')
      setSecondes(0)
      lancerSondage(data.requestId)

    } catch (e) {
      setMsg({ type: 'error', text: e.message })
    }
    setEnvoi(false)
  }

  // ── Attendre la confirmation ──────────────────────────────
  function lancerSondage(requestId) {
    chrono.current = setInterval(() => setSecondes(s => s + 1), 1000)

    sondage.current = setInterval(async () => {
      try {
        const token = await jetonSession()
        const r = await fetch(`/api/tranzak-check-status?requestId=${requestId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        const data = await r.json()

        if (data.status === 'SUCCESSFUL') { arreter(); setEtape('succes'); charger() }
        else if (['FAILED', 'CANCELLED', 'CANCELLED_BY_PAYER'].includes(data.status)) {
          arreter(); setEtape('echec')
        }
      } catch { /* on retentera au prochain tour */ }
    }, 5000)

    // Abandon au bout de 3 minutes
    setTimeout(() => {
      if (sondage.current) { arreter(); setEtape('echec') }
    }, 180000)
  }

  function arreter() {
    clearInterval(sondage.current); sondage.current = null
    clearInterval(chrono.current);  chrono.current = null
  }

  function recommencer() {
    arreter(); setEtape('choix'); setMsg(null); setSecondes(0)
  }

  // ── Repli manuel ──────────────────────────────────────────
  async function envoyerPreuve() {
    if (!refManuelle.trim()) {
      setMsg({ type: 'error', text: lang === 'fr' ? 'Entre le numéro de transaction.' : 'Enter transaction number.' })
      return
    }
    setEnvoi(true)
    const f = FORMULES.find(x => x.id === formule)
    const { error } = await soumettrePaiementManuel(user.id, {
      montant: f.montant, methode: methodeManuelle.id,
      reference: refManuelle.trim(), boutique_nom: boutique?.nom || ''
    })
    setEnvoi(false)
    if (error) { setMsg({ type: 'error', text: error.message }); return }
    setMsg({ type: 'success', text: lang === 'fr'
      ? '✓ Preuve envoyée — activation sous 24h.' : '✓ Proof sent — activated within 24h.' })
    setManuel(false); setMethodeManuelle(null); setRefManuelle('')
    charger()
  }

  const actif  = abonnement && new Date(abonnement.date_fin) > new Date() && abonnement.statut !== 'expire'
  const jours  = abonnement ? joursRestants(abonnement.date_fin) : 0
  const essai  = abonnement?.statut === 'trial'
  const choisie = FORMULES.find(f => f.id === formule)

  if (loading) return <div className="main"><div className="empty">Chargement...</div></div>

  return (
    <div className="main">
      <div className="page-title">{lang === 'fr' ? 'Abonnement' : 'Subscription'}</div>

      {/* ── STATUT ── */}
      <div style={{
        background: actif ? (jours <= 7 ? 'var(--amber-pale)' : 'var(--sage-pale)') : 'var(--rose-pale)',
        border: `1.5px solid ${actif ? (jours <= 7 ? '#E8C080' : 'var(--sage-light)') : '#DFA0A0'}`,
        borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 17 }}>
            {actif ? (essai ? "🎁 Période d'essai" : '✅ Abonnement actif') : '🔒 Abonnement expiré'}
          </div>
          <div style={{
            padding: '3px 12px', borderRadius: 50, fontSize: 11, fontWeight: 600, color: 'white',
            background: actif ? (jours <= 7 ? '#C4873A' : 'var(--sage)') : 'var(--rose)'
          }}>{actif ? `${jours}j` : 'Expiré'}</div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)' }}>
          {abonnement ? `${actif ? 'Expire' : 'Expiré'} le ${formatDate(abonnement.date_fin)}` : '—'}
        </div>
      </div>

      {/* ── CHOIX ── */}
      {etape === 'choix' && !manuel && (
        <>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, marginBottom: '1rem' }}>
            {lang === 'fr' ? 'Choisir une formule' : 'Choose a plan'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: '1.25rem' }}>
            {FORMULES.map(f => {
              const sel = formule === f.id
              return (
                <button key={f.id} onClick={() => setFormule(f.id)} style={{
                  padding: '16px 12px', borderRadius: 'var(--radius-lg)', cursor: 'pointer',
                  border: `2.5px solid ${sel ? 'var(--sage)' : 'var(--cream-dark)'}`,
                  background: sel ? 'var(--sage-pale)' : 'var(--warm-white)',
                  fontFamily: 'DM Sans, sans-serif', textAlign: 'center', position: 'relative'
                }}>
                  {f.badge_fr && (
                    <div style={{
                      position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                      background: 'var(--sage)', color: 'white', fontSize: 10, fontWeight: 700,
                      padding: '3px 10px', borderRadius: 50, whiteSpace: 'nowrap'
                    }}>{lang === 'fr' ? f.badge_fr : f.badge_en}</div>
                  )}
                  <div style={{ fontSize: 15, fontWeight: 700, marginTop: f.badge_fr ? 6 : 0, marginBottom: 8,
                    color: sel ? 'var(--sage-dark)' : 'var(--charcoal)' }}>
                    {lang === 'fr' ? f.label_fr : f.label_en}
                  </div>
                  <div style={{ fontFamily: 'Fraunces, serif', fontSize: 26, lineHeight: 1, marginBottom: 4,
                    color: sel ? 'var(--sage-dark)' : 'var(--charcoal)' }}>
                    {f.montant.toLocaleString('fr-FR')}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 6 }}>
                    FCFA / {lang === 'fr' ? f.unite_fr : f.unite_en}
                  </div>
                  {f.mois_fr && (
                    <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--sage)',
                      background: 'var(--sage-pale)', borderRadius: 50, padding: '2px 8px',
                      display: 'inline-block', marginBottom: 6 }}>
                      {lang === 'fr' ? f.mois_fr : f.mois_en}
                    </div>
                  )}
                  <div style={{ fontSize: 10, lineHeight: 1.5, color: sel ? 'var(--sage)' : 'var(--muted)' }}>
                    {lang === 'fr' ? f.desc_fr : f.desc_en}
                  </div>
                  {sel && (
                    <div style={{ position: 'absolute', top: 8, right: 10, width: 18, height: 18,
                      borderRadius: '50%', background: 'var(--sage)', color: 'white', fontSize: 10,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>✓</div>
                  )}
                </button>
              )
            })}
          </div>

          {/* Numéro Mobile Money */}
          <div style={{ background: 'var(--warm-white)', border: '1px solid var(--cream-dark)',
            borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem',
            boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, marginBottom: 4 }}>
              📱 {lang === 'fr' ? 'Payer par Mobile Money' : 'Pay with Mobile Money'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: '1rem', lineHeight: 1.7 }}>
              {lang === 'fr'
                ? 'MTN MoMo ou Orange Money. Tu recevras une demande de validation sur ton téléphone.'
                : 'MTN MoMo or Orange Money. You will receive a prompt on your phone.'}
            </div>

            <div className="field">
              <label style={{ fontSize: 12 }}>
                {lang === 'fr' ? 'Ton numéro Mobile Money' : 'Your Mobile Money number'}
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ padding: '10px 12px', background: 'var(--cream)',
                  borderRadius: 'var(--radius-md)', fontSize: 15, fontWeight: 600,
                  color: 'var(--muted)', whiteSpace: 'nowrap' }}>+237</div>
                <input type="tel" inputMode="numeric" placeholder="6 98 36 66 15"
                  value={numero} maxLength={13}
                  onChange={e => { setNumero(e.target.value); setMsg(null) }}
                  style={{ flex: 1, letterSpacing: 1, fontSize: 16 }} />
              </div>
            </div>

            {msg && (
              <div className={msg.type === 'success' ? 'success-msg' : 'error-msg'} style={{ marginBottom: 10 }}>
                {msg.text}
              </div>
            )}

            <button className="btn-primary" onClick={payer} disabled={envoi}>
              {envoi
                ? (lang === 'fr' ? 'Envoi en cours...' : 'Sending...')
                : `📱 ${lang === 'fr' ? 'Payer' : 'Pay'} ${choisie.montant.toLocaleString('fr-FR')} FCFA`}
            </button>

            <button onClick={() => { setManuel(true); setMsg(null) }} style={{
              width: '100%', marginTop: 10, padding: '9px', background: 'none', border: 'none',
              color: 'var(--muted)', fontSize: 12, cursor: 'pointer',
              fontFamily: 'DM Sans, sans-serif', textDecoration: 'underline'
            }}>
              {lang === 'fr' ? 'Payer manuellement' : 'Pay manually'}
            </button>
          </div>
        </>
      )}

      {/* ── ATTENTE ── */}
      {etape === 'attente' && (
        <div style={{ background: 'var(--amber-pale)', border: '1.5px solid #E8C080',
          borderRadius: 'var(--radius-lg)', padding: '2rem 1.5rem', textAlign: 'center', marginBottom: '1rem' }}>
          <div style={{ fontSize: 48, marginBottom: 14 }}>📲</div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 18, color: '#854F0B', marginBottom: 10 }}>
            {lang === 'fr' ? 'Vérifie ton téléphone' : 'Check your phone'}
          </div>
          <div style={{ fontSize: 13, color: '#633806', lineHeight: 1.8, marginBottom: '1.25rem' }}>
            {lang === 'fr'
              ? `Une demande de ${choisie.montant.toLocaleString('fr-FR')} FCFA a été envoyée au +237 ${numero}. Entre ton code secret pour valider.`
              : `A request for ${choisie.montant.toLocaleString('fr-FR')} FCFA was sent. Enter your PIN to confirm.`}
          </div>
          <div style={{ height: 4, background: 'rgba(133,79,11,0.15)', borderRadius: 2, marginBottom: 10, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min(100, (secondes / 180) * 100)}%`,
              background: '#C4873A', borderRadius: 2, transition: 'width 1s linear' }} />
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)' }}>
            {lang === 'fr' ? `En attente depuis ${secondes}s` : `Waiting ${secondes}s`}
          </div>
          <button onClick={recommencer} style={{
            marginTop: '1.25rem', padding: '9px 20px', borderRadius: 'var(--radius-md)',
            border: '1px solid #E8C080', background: 'var(--warm-white)', color: '#854F0B',
            fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif'
          }}>{lang === 'fr' ? 'Annuler' : 'Cancel'}</button>
        </div>
      )}

      {/* ── SUCCÈS ── */}
      {etape === 'succes' && (
        <div style={{ background: 'var(--sage-pale)', border: '1.5px solid var(--sage-light)',
          borderRadius: 'var(--radius-lg)', padding: '2rem 1.5rem', textAlign: 'center', marginBottom: '1rem' }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 19, color: 'var(--sage-dark)', marginBottom: 8 }}>
            {lang === 'fr' ? 'Paiement confirmé !' : 'Payment confirmed!'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
            {lang === 'fr'
              ? 'Ton abonnement est activé immédiatement. Merci !'
              : 'Your subscription is active. Thank you!'}
          </div>
          <button className="btn-primary" onClick={recommencer}>
            {lang === 'fr' ? 'Terminé' : 'Done'}
          </button>
        </div>
      )}

      {/* ── ÉCHEC ── */}
      {etape === 'echec' && (
        <div style={{ background: 'var(--rose-pale)', border: '1.5px solid #DFA0A0',
          borderRadius: 'var(--radius-lg)', padding: '1.75rem 1.5rem', textAlign: 'center', marginBottom: '1rem' }}>
          <div style={{ fontSize: 44, marginBottom: 12 }}>😕</div>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 17, color: 'var(--rose)', marginBottom: 8 }}>
            {lang === 'fr' ? 'Paiement non abouti' : 'Payment not completed'}
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1.25rem' }}>
            {lang === 'fr'
              ? 'La demande a expiré ou a été refusée. Vérifie ton solde et réessaie. Aucun montant n\'a été débité.'
              : 'The request expired or was declined. No amount was charged.'}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => { setManuel(true); setEtape('choix') }} style={{
              flex: 1, padding: '11px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
              border: '1px solid var(--cream-dark)', background: 'var(--warm-white)',
              color: 'var(--muted)', fontSize: 13, fontFamily: 'DM Sans, sans-serif'
            }}>{lang === 'fr' ? 'Payer manuellement' : 'Pay manually'}</button>
            <button className="btn-primary" style={{ flex: 1 }} onClick={recommencer}>
              {lang === 'fr' ? 'Réessayer' : 'Retry'}
            </button>
          </div>
        </div>
      )}

      {/* ── REPLI MANUEL ── */}
      {manuel && (
        <div style={{ background: 'var(--warm-white)', border: '1px solid var(--cream-dark)',
          borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem' }}>
          <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, marginBottom: '1rem' }}>
            {lang === 'fr' ? 'Paiement manuel' : 'Manual payment'}
          </div>

          {!methodeManuelle ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {METHODES_MANUELLES.map(m => (
                <button key={m.id} onClick={() => setMethodeManuelle(m)} style={{
                  padding: '14px 10px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
                  border: '1.5px solid var(--cream-dark)', background: 'var(--cream)',
                  fontFamily: 'DM Sans, sans-serif', textAlign: 'center'
                }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>{m.emoji}</div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{m.label}</div>
                </button>
              ))}
            </div>
          ) : (
            <>
              <div style={{ background: 'var(--sage-pale)', borderRadius: 'var(--radius-md)',
                padding: '14px', marginBottom: '1rem', textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>
                  {lang === 'fr' ? `Envoie ${choisie.montant.toLocaleString('fr-FR')} FCFA à` : `Send ${choisie.montant.toLocaleString('fr-FR')} FCFA to`}
                </div>
                <div style={{ fontFamily: 'Fraunces, serif', fontSize: 22, letterSpacing: 1 }}>
                  {methodeManuelle.numero}
                </div>
                <div style={{ fontSize: 11, color: 'var(--sage)', marginTop: 4 }}>
                  {lang === 'fr' ? 'Nom : ' : 'Name: '}{methodeManuelle.nom}
                </div>
              </div>

              <div className="field">
                <label style={{ fontSize: 12 }}>
                  {lang === 'fr' ? 'Numéro de transaction (reçu par SMS)' : 'Transaction number (from SMS)'}
                </label>
                <input type="text" placeholder="Ex: MP241201ABCD123" value={refManuelle}
                  onChange={e => { setRefManuelle(e.target.value); setMsg(null) }}
                  style={{ letterSpacing: 1 }} />
              </div>

              {msg && (
                <div className={msg.type === 'success' ? 'success-msg' : 'error-msg'} style={{ marginBottom: 10 }}>
                  {msg.text}
                </div>
              )}

              <button className="btn-primary" onClick={envoyerPreuve} disabled={envoi}>
                {envoi ? (lang === 'fr' ? 'Envoi...' : 'Sending...')
                       : (lang === 'fr' ? '✓ Envoyer la preuve' : '✓ Send proof')}
              </button>
            </>
          )}

          <button onClick={() => { setManuel(false); setMethodeManuelle(null); setMsg(null) }} style={{
            width: '100%', marginTop: 10, padding: '9px', background: 'none', border: 'none',
            color: 'var(--muted)', fontSize: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif'
          }}>← {lang === 'fr' ? 'Retour au paiement automatique' : 'Back to automatic payment'}</button>
        </div>
      )}

      {/* ── HISTORIQUE ── */}
      {paiements.length > 0 && (
        <>
          <div className="section-label">{lang === 'fr' ? 'Historique des paiements' : 'Payment history'}</div>
          {paiements.map(p => (
            <div key={p.id} style={{ background: 'var(--warm-white)', border: '1px solid var(--cream-dark)',
              borderRadius: 'var(--radius-md)', padding: '12px 14px', marginBottom: 8,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>
                  {p.methode === 'tranzak' ? '⚡' : p.methode === 'orange_money' ? '🟠' : '🟡'}{' '}
                  {p.montant?.toLocaleString('fr-FR')} FCFA
                  <span style={{ fontSize: 10, color: 'var(--muted)', marginLeft: 6 }}>
                    {p.montant >= 30000 ? (lang === 'fr' ? '· Annuel' : '· Annual')
                                        : (lang === 'fr' ? '· Mensuel' : '· Monthly')}
                  </span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>
                  {new Date(p.created_at).toLocaleDateString('fr-FR')}
                </div>
              </div>
              <div style={{
                padding: '4px 10px', borderRadius: 50, fontSize: 10, fontWeight: 600,
                background: p.statut === 'confirme' ? 'var(--sage-pale)'
                          : p.statut === 'rejete' ? 'var(--rose-pale)' : 'var(--amber-pale)',
                color: p.statut === 'confirme' ? 'var(--sage-dark)'
                     : p.statut === 'rejete' ? 'var(--rose)' : '#8A5C1A'
              }}>
                {p.statut === 'confirme' ? '✓ Confirmé' : p.statut === 'rejete' ? '✗ Échoué' : '⏳ En attente'}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  )
}
